/**
 * Insights ↔ Kaching variant bridge (2026-05-16).
 *
 * Kaching Bundles renders its own variant-select dropdowns inside
 * .kaching-bundles__bar-variant-selects. Those selects have no name /
 * id / onchange handler in the DOM — Kaching wires its own listeners
 * internally and updates which variant gets added to cart, but does
 * NOT emit a Shopify-standard variant-changed event that the main
 * product gallery (insights-product.min.js) listens for.
 *
 * Result: user picks a different colour in the bundle dropdown →
 * cart picks the right variant → BUT the main product gallery stays
 * on the original colour's image.
 *
 * This bridge listens for change events bubbling from Kaching's
 * select elements and triggers the same image-swap path the main
 * variant picker uses. Three fallback paths, in priority:
 *
 *   A. Dispatch a synthetic 'variant:changed' / 'variant:change' /
 *      'cart:variant-changed' event with the variantId — most apps
 *      and theme JS listen for these.
 *   B. Find the main product form's existing variant <input
 *      name="id"> hidden field, set its value to the new variantId,
 *      and dispatch a 'change' event on it. insights-product.min.js
 *      listens on form variant changes.
 *   C. Look up the variant URL (product.variants[*].featured_image)
 *      via /products/<handle>.js and directly swap the main gallery
 *      <img src> + thumbnail active state. Last-resort scrape path.
 *
 * Diagnostic logs prefixed [insights-kaching-bridge].
 */
(function () {
  'use strict';

  function log() {
    try {
      var args = ['[insights-kaching-bridge]'].concat([].slice.call(arguments));
      console.log.apply(console, args);
    } catch (e) {
      /* noop */
    }
  }

  // Match Kaching's variant select containers. The class on the bar:
  //   .kaching-bundles__bar-variant-selects   (parent wrapper)
  //   .kaching-bundles__bar-variant-select    (each <select>)
  // We also accept any <select> inside a kaching-bundles element as
  // a fallback if their class names shift.
  var KACHING_SELECT_SELECTOR =
    '.kaching-bundles__bar-variant-selects select, ' +
    '.kaching-bundles select, ' +
    '[class*="kaching-bundles"] select';

  function dispatchOnForm(variantId) {
    // Find any main product form on the page (Trampolin-rendered)
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return false;
    var hiddenId = form.querySelector('input[name="id"]');
    if (hiddenId) {
      hiddenId.value = variantId;
      hiddenId.dispatchEvent(new Event('change', { bubbles: true }));
      log('Strategy B — set form input[name=id] to', variantId, 'and dispatched change');
      return true;
    }
    return false;
  }

  function dispatchCustomEvents(variantId) {
    var detail = { variantId: String(variantId), variant_id: String(variantId) };
    ['variant:changed', 'variant:change', 'cart:variant-changed', 'productVariant:changed'].forEach(
      function (name) {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
      },
    );
    log('Strategy A — dispatched synthetic variant-changed events for', variantId);
  }

  var swapInFlight = false;
  function swapMainGalleryByVariant(variantId) {
    if (swapInFlight) return;
    swapInFlight = true;
    var productHandle = (function () {
      // Try ProductJson script — Trampolin's main-product section emits one.
      var jsonEl = document.querySelector('script[id^="ProductJson"]');
      if (jsonEl) {
        try {
          var p = JSON.parse(jsonEl.textContent || '{}');
          return p.handle;
        } catch (e) {
          /* fall through */
        }
      }
      // Fall back to scraping pathname.
      var m = window.location.pathname.match(/\/products\/([\w-]+)/);
      return m ? m[1] : null;
    })();
    if (!productHandle) {
      swapInFlight = false;
      return;
    }
    fetch('/products/' + productHandle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (product) {
        var variant = (product.variants || []).find(function (v) {
          return String(v.id) === String(variantId);
        });
        if (!variant) return;
        var imgUrl =
          variant.featured_image && variant.featured_image.src
            ? variant.featured_image.src
            : variant.featured_image || null;
        if (!imgUrl) return;
        // Trampolin gallery: main image + thumbnails. The main image has
        // the largest src or matches a known pattern. Try both common
        // shapes.
        var mainImg = document.querySelector(
          '.tl-product-gallery__main img, .tl-photo-gallery__main img, [data-tl-main-image] img, .product-gallery__media-active img',
        );
        if (mainImg) {
          var finalSrc = imgUrl.startsWith('//') ? 'https:' + imgUrl : imgUrl;
          mainImg.setAttribute('src', finalSrc);
          log('Strategy C — swapped main gallery <img src> to', finalSrc);
        }
      })
      .catch(function (err) {
        log('Strategy C — fetch /products/<handle>.js failed', err);
      })
      .finally(function () {
        swapInFlight = false;
      });
  }

  function onKachingSelectChange(e) {
    var target = e.target;
    if (!target || target.tagName !== 'SELECT') return;
    var variantId = target.value;
    if (!variantId) return;
    log('detected Kaching select change → variantId', variantId, target);

    // Strategy A — synthetic events (some themes / apps listen for these)
    dispatchCustomEvents(variantId);

    // Strategy B — directly drive the main form's hidden variant input
    var droveForm = dispatchOnForm(variantId);

    // Strategy C — explicit gallery swap as last-resort
    if (!droveForm) {
      swapMainGalleryByVariant(variantId);
    }
  }

  // Delegate via document so we catch selects added/removed dynamically.
  document.addEventListener(
    'change',
    function (e) {
      if (!e.target || !e.target.matches) return;
      if (!e.target.matches(KACHING_SELECT_SELECTOR)) return;
      onKachingSelectChange(e);
    },
    false,
  );

  log('script loaded — watching Kaching bundle bar variant selects');
})();
